import {isEmpty, omit, pickBy} from 'lodash-es';

import type {AfriexQueryParams} from '@/types';

// The applied list filters live in this query param, so opening a transaction
// and pressing Back returns to the list as it was filtered. A list opened
// fresh from the menu has no param and starts clean. Kept apart from the
// `status` param, which a dashboard link uses to lock the status filter.
const FILTERS_PARAM = 'filters';

export const readFiltersFromUrl = (search: string): AfriexQueryParams => {
  const raw = new URLSearchParams(search).get(FILTERS_PARAM);
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    // Every filter this list sends is a string (dates are ISO strings once
    // applied); anything else in a hand-edited URL is dropped.
    return pickBy(
      parsed as Record<string, unknown>,
      (value) => typeof value === 'string' && value !== '',
    );
  } catch {
    return {};
  }
};

// Replaces the current history entry (no new Back step) and keeps the
// router's own history state.
export const writeFiltersToUrl = (filters: AfriexQueryParams): void => {
  const params = new URLSearchParams(window.location.search);
  const applied = pickBy(filters, Boolean);
  if (isEmpty(applied)) {
    params.delete(FILTERS_PARAM);
  } else {
    params.set(FILTERS_PARAM, JSON.stringify(applied));
  }
  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}`,
  );
};

// The filter panel's staging state: everything restored except the status,
// which has its own select outside the panel.
export const toPanelFilters = (filters: AfriexQueryParams): AfriexQueryParams =>
  omit(filters, 'status');
